import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { ActionIcon, Loader } from "@mantine/core";
import "./Snackbar.css";
import {
	IconCircleCheckFilled,
	IconExclamationCircleFilled,
	IconInfoCircleFilled,
} from "@tabler/icons-react";
import { LexWorkplaceXIcon } from "./LexWorkplaceIcons";

const variantIcon = {
	success: IconCircleCheckFilled,
	warning: IconInfoCircleFilled,
	error: IconExclamationCircleFilled,
	info: IconInfoCircleFilled,
};

const variantStyles = {
	success: { backgroundColor: "#43a047" },
	error: { backgroundColor: "#d32f2f" },
	info: { backgroundColor: "var(--lxw-main-500)" },
	warning: { backgroundColor: "#ff8f00", color: "black" },
};

function CustomizedSnackbar({
	open,
	message,
	variant,
	autoHideDuration,
	onClose,
	loadingSpinnerVisible,
	allowClose,
	stackIndex,
}) {
	const timerRef = useRef(null);
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;

	useEffect(() => {
		if (open && autoHideDuration) {
			timerRef.current = setTimeout(() => {
				onCloseRef.current();
			}, autoHideDuration);
		}
		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
		};
	}, [open, autoHideDuration]);

	if (!open) return null;

	const Icon = variantIcon[variant];
	const colorStyle = variantStyles[variant] || variantStyles.info;

	return (
		<div
			className="snackbarSpacer"
			style={{
				position: "fixed",
				bottom: `calc(24px + (${stackIndex || 0} * 56px))`,
				left: 24,
				zIndex: 1400,
			}}
		>
			<div
				style={{
					...colorStyle,
					color: colorStyle.color || "#fff",
					display: "flex",
					alignItems: "center",
					padding: "6px 16px",
					borderRadius: 4,
					boxShadow:
						"0 3px 5px -1px rgba(0,0,0,.2), 0 6px 10px 0 rgba(0,0,0,.14), 0 1px 18px 0 rgba(0,0,0,.12)",
					flexWrap: "nowrap",
					minWidth: 288,
					maxWidth: 568,
				}}
			>
				<span style={{ display: "flex", alignItems: "center" }}>
					{loadingSpinnerVisible ? (
						<div className="snackbarProgressSpinner">
							<Loader size={30} color="white" />
						</div>
					) : (
						<Icon
							style={{
								opacity: 0.9,
								marginRight: 8,
								fontSize: 20,
								minWidth: 20,
								minHeight: 20,
							}}
						/>
					)}
					{message}
				</span>
				{allowClose ? (
					<ActionIcon
						variant="subtle"
						onClick={onClose}
						style={{
							height: 30,
							width: 30,
							margin: "0 5px",
							marginLeft: "auto",
							color: "inherit",
						}}
						aria-label="Close"
					>
						<LexWorkplaceXIcon />
					</ActionIcon>
				) : null}
			</div>
		</div>
	);
}

CustomizedSnackbar.propTypes = {
	message: PropTypes.node,
	onClose: PropTypes.func,
	variant: PropTypes.oneOf(["success", "warning", "error", "info"])
		.isRequired,
};

export default CustomizedSnackbar;
